<!-- The key form, written once and placed twice: under the row being replaced, or under a
     group with nothing for this scope yet. Only one is ever open, so it reads `draft`
     straight off the component. -->
<ng-template #keyForm>
  <div class="credential-form">
    <div class="dk-form-group">
      <label class="dk-form-group__label" for="credentialKey">Key</label>
      <input
        class="dk-field"
        id="credentialKey"
        type="password"
        autocomplete="off"
        placeholder="sk-ant-…"
        [(ngModel)]="draft"
        [disabled]="saving"
        [attr.aria-invalid]="formError ? 'true' : null"
        [attr.aria-describedby]="formError ? 'credentialKeyError' : null"
      />
      <p class="dk-form-group__hint">
        Checked with the provider before it is saved, so a mistyped key is caught here rather than in the middle of a
        conversation.
      </p>
      @if (formError) {
        <p class="dk-form-group__error" id="credentialKeyError" role="alert">{{ formError }}</p>
      }
    </div>

    <div class="row-actions">
      <button
        type="button"
        class="dk-btn dk-btn--sm"
        [disabled]="!canSave"
        [attr.data-loading]="saving ? '' : null"
        [attr.aria-busy]="saving"
        (click)="saveKey()"
      >
        {{ saving ? 'Checking…' : 'Save' }}
      </button>
      <button
        type="button"
        class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--sm"
        [disabled]="saving"
        (click)="closeForm()"
      >
        Cancel
      </button>
    </div>
  </div>
</ng-template>

@if (loading) {
  <p class="dk-caption settings-status" role="status">Loading…</p>
} @else if (loadError) {
  <p class="dk-form-group__error" role="alert">{{ loadError }}</p>
} @else {
  <section class="settings-section">
    <h2 class="dk-heading-4">API keys</h2>
    <hr class="dk-divider" />
    <p class="dk-caption">
      @if (scopedToProject) {
        Used by this project only. Without one, {{ projectName }} bills to the organization's key.
      } @else {
        What each run bills to. A project's own key wins over the organization's, and anything without one falls back to
        it.
      }
    </p>

    @for (group of keyGroups; track group.provider) {
      <h3 class="dk-body-sm dk-font-medium group-label">{{ group.label }}</h3>

      @for (row of group.rows; track row.id) {
        <div class="credential-row">
          <p class="dk-body-sm dk-font-medium">{{ scopeLabel(row) }}</p>
          <!-- The last four characters: enough to tell two keys apart, which is the only
               question anyone can ask of a key they cannot read back. -->
          <p class="dk-caption credential-value">••••••••{{ row.hint }}</p>
          <p class="dk-caption credential-row__meta">
            Changed {{ row.updated_at | date: 'mediumDate' }}
            @if (row.added_by) {
              · {{ row.added_by }}
            }
          </p>
          <div class="row-actions">
            <button
              type="button"
              class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--sm"
              [disabled]="checking === formKey(row.provider, row.project_id)"
              [attr.data-loading]="checking === formKey(row.provider, row.project_id) ? '' : null"
              [attr.aria-busy]="checking === formKey(row.provider, row.project_id)"
              (click)="testKey(row)"
            >
              {{ checking === formKey(row.provider, row.project_id) ? 'Testing…' : 'Test' }}
            </button>
            <button
              type="button"
              class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--sm"
              (click)="openForm(row.provider, row.project_id)"
            >
              Replace
            </button>
            <button
              type="button"
              class="dk-btn dk-btn--destructive dk-btn--outline dk-btn--sm"
              (click)="removeKey(row)"
            >
              Remove
            </button>
          </div>
        </div>

        @if (checkedKey === formKey(row.provider, row.project_id)) {
          <p class="dk-caption credential-verdict" [class.dk-text-destructive]="checkFailed" role="status">
            {{ checkMessage }}
          </p>
        }

        @if (form?.key === formKey(row.provider, row.project_id)) {
          <ng-container [ngTemplateOutlet]="keyForm" />
        }
      }

      <!-- Reachable whatever else exists: an organization whose only key sits on a project
           still needs a way to add the one everything else falls back to. -->
      @if (!group.ownScope) {
        @if (form?.key === formKey(group.provider, projectId)) {
          <ng-container [ngTemplateOutlet]="keyForm" />
        } @else {
          <button
            type="button"
            class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--sm credential-add"
            (click)="openForm(group.provider, projectId)"
          >
            {{ scopedToProject ? 'Add a key for this project' : 'Add an organization key' }}
          </button>
        }
      }
    }
  </section>

  <section class="settings-section">
    <h2 class="dk-heading-4">MCP tokens</h2>
    <hr class="dk-divider" />
    <p class="dk-caption">
      @if (scopedToProject) {
        Authorize for this project alone. Without its own authorization, {{ projectName }}
        uses the organization's.
      } @else {
        What the agent may reach. Whatever is picked when authorizing is the boundary — anything left out is
        unreachable.
      }
    </p>

    @for (group of tokenGroups; track group.provider) {
      <h3 class="dk-body-sm dk-font-medium group-label">{{ group.label }}</h3>

      @for (row of group.rows; track row.id) {
        <div class="credential-row">
          <p class="dk-body-sm dk-font-medium">{{ scopeLabel(row) }}</p>
          <p class="dk-caption">
            @if (row.registration_expired) {
              Authorization expired
            } @else {
              Connected
            }
          </p>
          <p class="dk-caption credential-row__meta">
            Since {{ row.created_at | date: 'mediumDate' }}
            <!-- The access token renews itself and is not worth a line. This date is the
                 end of the grant, and nothing can extend it. -->
            @if (row.registration_expires_at) {
              · until {{ row.registration_expires_at | date: 'mediumDate' }}
            }
            @if (row.added_by) {
              · {{ row.added_by }}
            }
          </p>
          <div class="row-actions">
            @if (row.registration_expired) {
              <button
                type="button"
                class="dk-btn dk-btn--sm"
                [disabled]="connecting === formKey(row.provider, row.project_id)"
                [attr.data-loading]="connecting === formKey(row.provider, row.project_id) ? '' : null"
                [attr.aria-busy]="connecting === formKey(row.provider, row.project_id)"
                (click)="connect(row.provider, row.project_id)"
              >
                {{ connecting === formKey(row.provider, row.project_id) ? 'Opening…' : 'Reauthorize' }}
              </button>
            }
            <button
              type="button"
              class="dk-btn dk-btn--destructive dk-btn--outline dk-btn--sm"
              (click)="disconnect(row)"
            >
              Disconnect
            </button>
          </div>
        </div>

        <!-- Nothing fills `resources` yet: knowing what a grant covers means asking the MCP
             server, which only the agent service can do. The date is all that can be said
             honestly until its shape is known, and rendering the blob raw would be a
             placeholder pretending to be a feature. -->
        @if (row.resources_synced_at) {
          <p class="dk-caption credential-verdict">
            Sites and pages last checked {{ row.resources_synced_at | date: 'mediumDate' }}
          </p>
        }
      }

      @if (!group.ownScope) {
        <button
          type="button"
          class="dk-btn dk-btn--success dk-btn--sm credential-add"
          [disabled]="connecting === formKey(group.provider, projectId)"
          [attr.data-loading]="connecting === formKey(group.provider, projectId) ? '' : null"
          [attr.aria-busy]="connecting === formKey(group.provider, projectId)"
          (click)="connect(group.provider, projectId)"
        >
          @if (connecting === formKey(group.provider, projectId)) {
            Opening…
          } @else {
            {{ scopedToProject ? 'Connect for this project' : 'Connect for the organization' }}
          }
        </button>
      }
    }

    @if (mcpError) {
      <p class="dk-form-group__error" role="alert">{{ mcpError }}</p>
    }
  </section>
}
